// Keyboard input for the turn-queue model (player request 2026-06-12).
//
// A TAP enqueues exactly one turn. HOLDING a key keeps enqueueing at a
// speed-proportional rate (handled in game.ts). The queue is never cleared
// by input — taps and holds only ever append.
//
// Keys: A / ArrowLeft = left, D / ArrowRight = right.

import type { DirectionPref } from '@evox/shared';

export interface InputSource {
  /** Turns tapped since the last frame (drained on read). */
  drainTaps(): DirectionPref[];
  /** Direction currently physically held, if any. */
  getHeld(): DirectionPref | undefined;
  dispose(): void;
}

const KEY_MAP: Record<string, DirectionPref> = {
  a: 'left',
  arrowleft: 'left',
  d: 'right',
  arrowright: 'right',
};

export function createKeyboardInput(): InputSource {
  let taps: DirectionPref[] = [];
  const down = new Map<string, DirectionPref>(); // physical keys currently down
  let held: DirectionPref | undefined;

  function onKeyDown(e: KeyboardEvent): void {
    const dir = KEY_MAP[e.key.toLowerCase()];
    if (dir === undefined) return;
    e.preventDefault();
    if (e.repeat) return; // OS auto-repeat is not a tap
    taps.push(dir);
    down.set(e.key.toLowerCase(), dir);
    held = dir; // most recent press wins
  }

  function onKeyUp(e: KeyboardEvent): void {
    const key = e.key.toLowerCase();
    if (!(key in KEY_MAP)) return;
    down.delete(key);
    if (held === KEY_MAP[key]) {
      // Fall back to any other key still held.
      const remaining = [...down.values()];
      held = remaining.length > 0 ? remaining[remaining.length - 1] : undefined;
    }
  }

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return {
    drainTaps() {
      const out = taps;
      taps = [];
      return out;
    },
    getHeld: () => held,
    dispose() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    },
  };
}
